// Device-specific performance optimizations
export interface DeviceCapabilities {
  isMobile: boolean;
  isLowEnd: boolean;
  connectionSpeed: 'slow' | 'medium' | 'fast';
  memoryLevel: 'low' | 'medium' | 'high';
}

export const detectDeviceCapabilities = (): DeviceCapabilities => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Detect low-end devices
  const hardwareConcurrency = navigator.hardwareConcurrency || 1;
  const deviceMemory = (navigator as { deviceMemory?: number }).deviceMemory || 4; // Default to 4GB
  const isLowEnd = hardwareConcurrency <= 2 || deviceMemory <= 2;
  
  // Detect connection speed
  const connection = (navigator as { connection?: { effectiveType?: string } }).connection;
  let connectionSpeed: 'slow' | 'medium' | 'fast' = 'medium';
  
  if (connection) {
    const effectiveType = connection.effectiveType;
    if (effectiveType === 'slow-2g' || effectiveType === '2g') {
      connectionSpeed = 'slow';
    } else if (effectiveType === '3g') {
      connectionSpeed = 'medium';
    } else {
      connectionSpeed = 'fast';
    }
  }
  
  // Determine memory level
  let memoryLevel: 'low' | 'medium' | 'high' = 'medium';
  if (deviceMemory <= 2) {
    memoryLevel = 'low';
  } else if (deviceMemory >= 8) {
    memoryLevel = 'high';
  }
  
  return {
    isMobile,
    isLowEnd,
    connectionSpeed,
    memoryLevel
  };
};

export const getOptimizedSettings = (capabilities: DeviceCapabilities) => {
  const { isMobile, isLowEnd, connectionSpeed, memoryLevel } = capabilities;
  
  return {
    // Delay settings for LazySection based on device
    categoryDelay: isLowEnd ? 800 : isMobile ? 400 : 200,
    organizersDelay: isLowEnd ? 1200 : isMobile ? 600 : 400,
    citiesDelay: isLowEnd ? 1600 : isMobile ? 800 : 600,
    
    // Cache TTL based on connection speed
    cacheTTL: connectionSpeed === 'slow' ? 300000 : connectionSpeed === 'medium' ? 180000 : 60000, // 5min, 3min, 1min
    
    // Request concurrency limits
    maxConcurrentRequests: isLowEnd ? 2 : isMobile ? 4 : 6,
    
    // Image loading strategy
    imageLoadingStrategy: connectionSpeed === 'slow' ? 'lazy' : 'eager',
    imageQuality: connectionSpeed === 'slow' ? 0.7 : 0.9,
    
    // Component rendering strategy
    useVirtualization: memoryLevel === 'low',
    batchSize: memoryLevel === 'low' ? 10 : memoryLevel === 'medium' ? 20 : 50,
    
    // Animation preferences
    reduceAnimations: isLowEnd || memoryLevel === 'low',
    
    // Preloading strategy
    enablePreloading: !isLowEnd && connectionSpeed !== 'slow',
    preloadNextPage: connectionSpeed === 'fast' && memoryLevel === 'high'
  };
};

// Global device optimization state
let deviceCapabilities: DeviceCapabilities | null = null;
let optimizedSettings: ReturnType<typeof getOptimizedSettings> | null = null;

export const initializeDeviceOptimization = () => {
  deviceCapabilities = detectDeviceCapabilities();
  optimizedSettings = getOptimizedSettings(deviceCapabilities);
  
  console.log('🔧 Device optimization initialized:', {
    capabilities: deviceCapabilities,
    settings: optimizedSettings
  });
  
  return { deviceCapabilities, optimizedSettings };
};

export const getDeviceOptimizedSettings = () => {
  if (!optimizedSettings) {
    initializeDeviceOptimization();
  }
  return optimizedSettings!;
};

export const isLowEndDevice = (): boolean => {
  if (!deviceCapabilities) {
    initializeDeviceOptimization();
  }
  return deviceCapabilities!.isLowEnd;
};

export const isMobileDevice = (): boolean => {
  if (!deviceCapabilities) {
    initializeDeviceOptimization();
  }
  return deviceCapabilities!.isMobile;
};

// Performance monitoring
export const logPerformanceMetrics = () => {
  if ('performance' in window) {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    const paintEntries = performance.getEntriesByType('paint');
    
    const metrics = {
      // Page load metrics
      domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
      loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
      
      // Paint metrics
      firstPaint: paintEntries.find(entry => entry.name === 'first-paint')?.startTime || 0,
      firstContentfulPaint: paintEntries.find(entry => entry.name === 'first-contentful-paint')?.startTime || 0,
      
      // Resource metrics
      totalRequests: performance.getEntriesByType('resource').length,
      
      // Device info
      deviceCapabilities: deviceCapabilities || 'not-initialized'
    };
    
    console.log('📊 Performance Metrics:', metrics);
    return metrics;
  }
  
  return null;
};
